import {
  AutofillType,
  CreateOrderResponse,
  Tkv2PostOrderInput,
  XMLPropertyResult,
  expected,
} from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useCreateTkv2OrderAPI } from '../../../hooks/api/schema-hooks/useCreateTkv2OrderAPI.js';
import { usePostVacantLandSales } from '../../../hooks/api/toolkitComponents.js';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.js';
import { useToaster } from '../../../hooks/useToaster/useToaster.js';

export function useCreateOrder(): (
  input: Tkv2PostOrderInput,
  autofillType?: AutofillType,
  xmlSubjectProperty?: XMLPropertyResult,
) => Promise<CreateOrderResponse> {
  const { queryKeyFn } = useToolkitContext();
  const createOrderQuery = useCreateTkv2OrderAPI();
  const postVacantLandSales = usePostVacantLandSales();
  const queryClient = useQueryClient();
  const makeToast = useToaster();

  return async (
    order: Tkv2PostOrderInput,
    autofillType: AutofillType | undefined,
    xmlSubjectProperty?: XMLPropertyResult,
  ): Promise<CreateOrderResponse> => {
    try {
      // Filter out unrelated info based on autofill type
      const input: Tkv2PostOrderInput =
        autofillType !== 'listingID'
          ? {
              ...order,
              mls: undefined,
              listingID: undefined,
              supplementWithPublicRecord: undefined,
            }
          : order;

      const res = await createOrderQuery.mutateAsync({
        body: {
          input: {
            ...input,
            version: 2,
          },
          subject: xmlSubjectProperty,
        },
      });

      if (res.warnings?.length ?? 0 > 0) {
        makeToast({
          type: 'warning',
          id: 'create-order-warning',
          title: res.warnings?.join('\n') ?? '',
        });
      }

      const orderID = expected(res.id);

      postVacantLandSales.mutate(
        {
          pathParams: {
            orderID,
          },
        },
        {
          onSuccess: () => {
            // Refresh vacant land sales on success
            queryClient.refetchQueries({
              queryKey: queryKeyFn({
                operationId: 'getVacantLandSales',
                path: '/api/v2/orders/{orderID}/attachments/vacantLandSales',
                variables: {
                  pathParams: {
                    orderID,
                  },
                },
              }),
            });
          },
        },
      );
      return res;
    } catch (err: any) {
      console.error(err);
      const message = err?.stack?.message ?? 'Error creating order';
      makeToast({
        type: 'error',
        id: 'create-order-error',
        title: message,
      });
      throw err;
    }
  };
}
